import { converter, formatHex, parse, toGamut, wcagContrast, type CuloriColor } from 'culori'
import type { BuiltInPaletteRecipeId, PaletteRecipeId, ThemeContrast, ThemeTokens } from '../../theme/presets'
import { DEFAULT_CUSTOM_RECIPE, runCustomTheme, type CustomRecipeDiagnostic } from './customRecipe'

export type { PaletteRecipeId, ThemeContrast } from '../../theme/presets'

export interface PaletteRecipe {
  id: PaletteRecipeId
  name: string
  description: string
}

export interface GeneratedThemeResult {
  tokens: ThemeTokens
  editorInk: string
  canvasContrast: number
  canvasPasses: boolean
  recipeDiagnostics: CustomRecipeDiagnostic[]
  contrastAdjustmentCount: number
}

interface OklchColor extends CuloriColor {
  mode: 'oklch'
  l: number
  c: number
  h: number
}

interface RecipePlan {
  accentHue: number
  secondaryHue: number
  surfaceHue: number
  accentChroma: number
  surfaceChroma: number
  surfaceStep: number
}

export const PALETTE_RECIPES: PaletteRecipe[] = [
  { id: 'ink-wash', name: 'Ink Wash', description: 'A quiet two-anchor palette with exponential tonal spacing.' },
  { id: 'tonal', name: 'Tonal', description: 'One hue with a restrained logarithmic lightness ramp.' },
  { id: 'analogous', name: 'Analogous', description: 'Related hues drift gently to either side of the anchors.' },
  { id: 'split-complement', name: 'Split Complement', description: 'Two softened opposing hues create lively contrast.' },
  { id: 'triadic', name: 'Triadic', description: 'Three evenly spaced hue families create balanced color contrast.' },
  { id: 'golden-angle', name: 'Golden Angle', description: 'Irrational hue spacing gives accents the most separation.' },
  { id: 'custom', name: 'Custom', description: 'Program the full semantic theme in sandboxed JavaScript.' },
]

const toOklch = converter('oklch')
const gamutMap = toGamut('rgb')

function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value))
}

function wrapHue(hue: number) {
  return ((hue % 360) + 360) % 360
}

function shortestHue(a: number, b: number, amount: number) {
  const delta = ((b - a + 540) % 360) - 180
  return wrapHue(a + delta * amount)
}

function asOklch(input: string, fallback: string): OklchColor {
  const converted = toOklch(parse(input) || parse(fallback)!)
  return {
    mode: 'oklch',
    l: clamp(converted?.l ?? 0.5),
    c: Math.max(0, converted?.c ?? 0),
    h: wrapHue(converted?.h ?? 250),
  }
}

function mapped(color: OklchColor): CuloriColor {
  return gamutMap(color) || { mode: 'rgb', r: 0, g: 0, b: 0 }
}

function hex(color: OklchColor): string {
  return formatHex(mapped(color)) || '#000000'
}

function rgbChannels(color: OklchColor): string {
  const rgb = mapped(color)
  return [rgb.r, rgb.g, rgb.b]
    .map(channel => Math.round(clamp(channel ?? 0) * 255))
    .join(', ')
}

function contrast(a: OklchColor, b: OklchColor) {
  return wcagContrast(mapped(a), mapped(b))
}

function mix(a: OklchColor, b: OklchColor, amount: number): OklchColor {
  const t = clamp(amount)
  const hue = a.c < 0.005 ? b.h : b.c < 0.005 ? a.h : shortestHue(a.h, b.h, t)
  return {
    mode: 'oklch',
    l: a.l + (b.l - a.l) * t,
    c: a.c + (b.c - a.c) * t,
    h: hue,
  }
}

function withContrast(candidate: OklchColor, background: OklchColor, target: number): OklchColor {
  // Leave a small margin because the final CSS colors are quantized to 8-bit
  // sRGB; a mathematically exact boundary can otherwise round just below it.
  const guardedTarget = target + 0.08
  if (contrast(candidate, background) >= guardedTarget) return candidate

  let best: OklchColor | null = null
  let bestDistance = Infinity
  for (let step = 0; step <= 200; step += 1) {
    const l = step / 200
    const attempt = { ...candidate, l }
    if (contrast(attempt, background) >= guardedTarget) {
      const distance = Math.abs(l - candidate.l)
      if (distance < bestDistance) {
        best = attempt
        bestDistance = distance
      }
    }
  }

  if (best) return best
  const black: OklchColor = { mode: 'oklch', l: 0, c: 0, h: candidate.h }
  const white: OklchColor = { mode: 'oklch', l: 1, c: 0, h: candidate.h }
  return contrast(white, background) >= contrast(black, background) ? white : black
}

function mostReadable(background: OklchColor, dark: OklchColor, light: OklchColor): OklchColor {
  return contrast(dark, background) >= contrast(light, background) ? dark : light
}

function anchorHue(background: OklchColor, foreground: OklchColor) {
  if (background.c < 0.012 && foreground.c < 0.012) return 250
  if (background.c < 0.012) return foreground.h
  if (foreground.c < 0.012) return background.h
  const weight = foreground.c / (background.c + foreground.c)
  return shortestHue(background.h, foreground.h, weight)
}

function makeBuiltInRecipe(id: BuiltInPaletteRecipeId, background: OklchColor, foreground: OklchColor): RecipePlan {
  const base = anchorHue(background, foreground)
  const sharedSurface = background.c < 0.012 ? base : background.h
  const plans: Record<BuiltInPaletteRecipeId, RecipePlan> = {
    'ink-wash': {
      accentHue: foreground.c >= 0.012 ? foreground.h : base,
      secondaryHue: background.c >= 0.012 ? background.h : wrapHue(base + 42),
      surfaceHue: shortestHue(sharedSurface, base, 0.35),
      accentChroma: 0.15,
      surfaceChroma: Math.min(0.045, background.c * 0.8 + 0.008),
      surfaceStep: 0.055,
    },
    tonal: {
      accentHue: base,
      secondaryHue: base,
      surfaceHue: base,
      accentChroma: 0.13,
      surfaceChroma: Math.min(0.04, Math.max(0.012, background.c)),
      surfaceStep: 0.045,
    },
    analogous: {
      accentHue: wrapHue(base + 30),
      secondaryHue: wrapHue(base - 30),
      surfaceHue: sharedSurface,
      accentChroma: 0.145,
      surfaceChroma: Math.min(0.04, background.c + 0.008),
      surfaceStep: 0.05,
    },
    'split-complement': {
      accentHue: wrapHue(base + 150),
      secondaryHue: wrapHue(base + 210),
      surfaceHue: sharedSurface,
      accentChroma: 0.16,
      surfaceChroma: Math.min(0.035, background.c + 0.006),
      surfaceStep: 0.052,
    },
    triadic: {
      accentHue: wrapHue(base + 120),
      secondaryHue: wrapHue(base + 240),
      surfaceHue: shortestHue(sharedSurface, wrapHue(base + 240), 0.18),
      accentChroma: 0.155,
      surfaceChroma: Math.min(0.038, background.c + 0.007),
      surfaceStep: 0.054,
    },
    'golden-angle': {
      accentHue: wrapHue(base + 137.507764),
      secondaryHue: wrapHue(base + 275.015528),
      surfaceHue: sharedSurface,
      accentChroma: 0.17,
      surfaceChroma: Math.min(0.035, background.c + 0.004),
      surfaceStep: 0.058,
    },
  }
  return plans[id]
}

function makeRecipe(
  id: PaletteRecipeId,
  background: OklchColor,
  foreground: OklchColor,
): { plan: RecipePlan; diagnostics: CustomRecipeDiagnostic[] } {
  return { plan: makeBuiltInRecipe(id === 'custom' ? 'ink-wash' : id, background, foreground), diagnostics: [] }
}

function accentCandidate(h: number, c: number, darkTheme: boolean): OklchColor {
  return { mode: 'oklch', l: darkTheme ? 0.66 : 0.5, c, h }
}

export function measureCanvasContrast(background: string, foreground: string): number {
  return contrast(asOklch(background, '#ffffff'), asOklch(foreground, '#000000'))
}

interface AutoInkPlan {
  hueOffset: number
  chroma: number
}

const AUTO_INK_PLANS: Record<BuiltInPaletteRecipeId, AutoInkPlan> = {
  'ink-wash': { hueOffset: 18, chroma: 0.012 },
  tonal: { hueOffset: 0, chroma: 0.025 },
  analogous: { hueOffset: -32, chroma: 0.035 },
  'split-complement': { hueOffset: 180, chroma: 0.042 },
  triadic: { hueOffset: 120, chroma: 0.04 },
  'golden-angle': { hueOffset: 137.507764, chroma: 0.045 },
}

/**
 * Derive a restrained, background-related reading color. Auto Ink treats the
 * canvas as inviolable and aims for 7:1 even when the interface uses Standard.
 */
export interface DerivedAutoInkResult {
  editorInk: string
  diagnostics: CustomRecipeDiagnostic[]
  contrastAdjusted: boolean
}

export function deriveAutoInk(
  backgroundInput: string,
  recipeId: PaletteRecipeId = 'ink-wash',
): DerivedAutoInkResult {
  const background = asOklch(backgroundInput, '#f0e3c4')
  const diagnostics: CustomRecipeDiagnostic[] = []
  const plan = AUTO_INK_PLANS[recipeId === 'custom' ? 'ink-wash' : recipeId]
  const hue = wrapHue(background.h + plan.hueOffset)
  const dark: OklchColor = { mode: 'oklch', l: 0.14, c: plan.chroma, h: hue }
  const light: OklchColor = { mode: 'oklch', l: 0.94, c: plan.chroma, h: hue }
  const candidate = mostReadable(background, dark, light)
  const adjusted = contrast(candidate, background) < 7.08
  return { editorInk: hex(withContrast(candidate, background, 7)), diagnostics, contrastAdjusted: adjusted }
}

export function generateAutoInkTheme(
  backgroundInput: string,
  recipeId: PaletteRecipeId = 'ink-wash',
  contrastMode: ThemeContrast = 'standard',
  accentOverride?: string,
  customRecipe: string = DEFAULT_CUSTOM_RECIPE,
): GeneratedThemeResult {
  if (recipeId === 'custom') {
    const custom = runCustomTheme(customRecipe, backgroundInput, '#000000', 'canvas-only', contrastMode)
    const canvasContrast = measureCanvasContrast(backgroundInput, custom.editorInk)
    return { ...custom, canvasContrast, canvasPasses: canvasContrast >= 7, recipeDiagnostics: custom.diagnostics, contrastAdjustmentCount: 0 }
  }
  const autoInk = deriveAutoInk(backgroundInput, recipeId)
  const result = generateTheme(backgroundInput, autoInk.editorInk, recipeId, contrastMode, accentOverride, customRecipe)
  return {
    ...result,
    editorInk: autoInk.editorInk,
    recipeDiagnostics: [...autoInk.diagnostics, ...result.recipeDiagnostics],
    contrastAdjustmentCount: result.contrastAdjustmentCount + (autoInk.contrastAdjusted ? 1 : 0),
  }
}

export function generateTheme(
  backgroundInput: string,
  foregroundInput: string,
  recipeId: PaletteRecipeId = 'ink-wash',
  contrastMode: ThemeContrast = 'standard',
  accentOverride?: string,
  customRecipe: string = DEFAULT_CUSTOM_RECIPE,
): GeneratedThemeResult {
  if (recipeId === 'custom') {
    const custom = runCustomTheme(customRecipe, backgroundInput, foregroundInput, 'canvas-and-ink', contrastMode)
    const canvasContrast = measureCanvasContrast(backgroundInput, custom.editorInk)
    const target = contrastMode === 'high' ? 7 : 4.5
    return { ...custom, canvasContrast, canvasPasses: canvasContrast >= target, recipeDiagnostics: custom.diagnostics, contrastAdjustmentCount: 0 }
  }
  const canvasBackground = asOklch(backgroundInput, '#f0e3c4')
  const foregroundAnchor = asOklch(foregroundInput, '#2b2115')
  const canvasContrast = contrast(canvasBackground, foregroundAnchor)
  const textTarget = contrastMode === 'high' ? 7 : 4.5
  const mutedTarget = contrastMode === 'high' ? 4.5 : 3.5
  // The editor retains the user's exact canvas color. The surrounding UI may
  // move its lightness to the nearest reachable contrast-safe surface.
  let contrastAdjustmentCount = 0
  const protectedColor = (candidate: OklchColor, against: OklchColor, target: number) => {
    if (contrast(candidate, against) < target + 0.08) contrastAdjustmentCount += 1
    return withContrast(candidate, against, target)
  }
  const background = protectedColor(canvasBackground, foregroundAnchor, textTarget)
  const darkTheme = background.l < foregroundAnchor.l
  const recipeResult = makeRecipe(recipeId, canvasBackground, foregroundAnchor)
  const plan = recipeResult.plan

  const foreground = protectedColor(foregroundAnchor, background, textTarget)
  const rawSurfaceRaised: OklchColor = {
    mode: 'oklch',
    l: clamp(background.l + (darkTheme ? plan.surfaceStep : -plan.surfaceStep)),
    c: plan.surfaceChroma,
    h: plan.surfaceHue,
  }
  const surfaceRaised = protectedColor(rawSurfaceRaised, foregroundAnchor, textTarget)
  const textBright = protectedColor(foreground, surfaceRaised, textTarget)
  const textNormal = protectedColor(mix(textBright, background, 0.12), surfaceRaised, textTarget)
  const textMuted = protectedColor(mix(textBright, background, 0.34), surfaceRaised, mutedTarget)
  const textFaint = protectedColor(mix(textBright, background, 0.5), surfaceRaised, 3)
  const border = protectedColor({ ...textBright, c: Math.min(textBright.c, 0.025) }, surfaceRaised, 3)
  const divider = protectedColor(mix(textBright, background, 0.58), background, 3)

  const overrideAccent = accentOverride ? asOklch(accentOverride, '#ff6b45') : null
  const rawAccent = overrideAccent
    ? { ...overrideAccent, c: Math.min(0.2, overrideAccent.c) }
    : accentCandidate(plan.accentHue, plan.accentChroma, darkTheme)
  const accentLink = protectedColor(rawAccent, surfaceRaised, textTarget)
  // One accessible brand color can safely serve as both a foreground accent
  // and a fill now that filled controls use the separate contrast token.
  const accentFill = accentLink
  const black: OklchColor = { mode: 'oklch', l: 0.08, c: 0.01, h: plan.accentHue }
  const white: OklchColor = { mode: 'oklch', l: 0.98, c: 0.005, h: plan.accentHue }
  const accentContrast = mostReadable(accentFill, black, white)
  const accentHover = mix(accentFill, accentContrast, 0.12)
  const accentActive = mix(accentFill, darkTheme ? black : white, 0.13)
  const accentLight = mix(accentFill, accentContrast, 0.28)

  const rawSecondary = accentCandidate(plan.secondaryHue, plan.accentChroma * 0.68, darkTheme)
  const secondary = protectedColor(rawSecondary, textBright, textTarget)
  const secondaryContrast = mostReadable(secondary, black, white)
  const rawPanel: OklchColor = {
    mode: 'oklch',
    l: clamp(background.l + (darkTheme ? plan.surfaceStep * 1.45 : -plan.surfaceStep * 1.45)),
    c: Math.min(0.065, plan.surfaceChroma + 0.022),
    h: plan.secondaryHue,
  }
  const panel = protectedColor(rawPanel, foregroundAnchor, textTarget)
  const panelText = protectedColor(foregroundAnchor, panel, textTarget)
  const panelMuted = protectedColor(mix(panelText, panel, 0.34), panel, mutedTarget)
  const panelBorder = protectedColor(mix(panelText, panel, 0.56), panel, 3)
  const panelHover = mix(panel, panelText, 0.1)
  const panelControl = mix(panel, panelText, 0.07)
  const panelAccent = protectedColor(rawAccent, panel, textTarget)
  const rawPanelActive = accentCandidate(plan.accentHue, plan.accentChroma * 0.85, darkTheme)
  const panelActive = protectedColor(rawPanelActive, panel, 3)
  const panelActiveText = mostReadable(panelActive, black, white)

  const status = (h: number, c: number) => protectedColor(
    { mode: 'oklch', l: darkTheme ? 0.72 : 0.48, c, h },
    surfaceRaised,
    textTarget,
  )
  const error = status(25, 0.19)
  const success = status(150, 0.16)
  const warning = status(85, 0.17)
  // Revision marks: typewriter teal for added text, ribbon red for removed.
  // Removed text is struck through and secondary, so it may sit at the muted
  // contrast level; added text reads as body copy.
  const diffAdded = protectedColor({ mode: 'oklch', l: darkTheme ? 0.84 : 0.48, c: 0.1, h: 185 }, surfaceRaised, textTarget)
  const diffRemoved = protectedColor({ mode: 'oklch', l: darkTheme ? 0.74 : 0.52, c: 0.15, h: 28 }, surfaceRaised, mutedTarget)
  const shadow = darkTheme ? black : { ...black, h: plan.surfaceHue }
  const overlayChannel = darkTheme ? '255, 255, 255' : '0, 0, 0'

  const tokens: ThemeTokens = {
    '--accent': hex(accentFill),
    '--accent-rgb': rgbChannels(accentFill),
    '--accent-link': hex(accentLink),
    '--accent-contrast': hex(accentContrast),
    '--accent-light': hex(accentLight),
    '--accent-hover': hex(accentHover),
    '--accent-active': hex(accentActive),
    '--secondary': hex(secondary),
    '--secondary-rgb': rgbChannels(secondary),
    '--secondary-contrast': hex(secondaryContrast),
    '--surface-bg': hex(background),
    '--surface-raised': hex(surfaceRaised),
    '--surface-border': hex(border),
    '--panel-bg': hex(panel),
    '--panel-text': hex(panelText),
    '--panel-muted': hex(panelMuted),
    '--panel-border': hex(panelBorder),
    '--panel-hover-bg': hex(panelHover),
    '--panel-control-bg': hex(panelControl),
    '--panel-accent': hex(panelAccent),
    '--panel-accent-rgb': rgbChannels(panelAccent),
    '--panel-active-bg': hex(panelActive),
    '--panel-active-text': hex(panelActiveText),
    '--control-bg': hex(surfaceRaised),
    '--control-text': hex(textNormal),
    '--control-border': hex(border),
    '--color-scheme': darkTheme ? 'dark' : 'light',
    '--text-bright': hex(textBright),
    '--text-bright-rgb': rgbChannels(textBright),
    '--text-normal': hex(textNormal),
    '--text-muted': hex(textMuted),
    '--text-faint': hex(textFaint),
    '--divider': hex(divider),
    '--error': hex(error),
    '--error-light': hex(mix(error, textBright, 0.2)),
    '--error-rgb': rgbChannels(error),
    '--success': hex(success),
    '--success-rgb': rgbChannels(success),
    '--warning': hex(warning),
    '--warning-light': hex(mix(warning, textBright, 0.22)),
    '--warning-rgb': rgbChannels(warning),
    '--diff-added': hex(diffAdded),
    '--diff-added-rgb': rgbChannels(diffAdded),
    '--diff-removed': hex(diffRemoved),
    '--diff-removed-rgb': rgbChannels(diffRemoved),
    '--button-hover-bg': `rgba(${overlayChannel}, ${darkTheme ? 0.14 : 0.07})`,
    '--button-active-bg': hex(textBright),
    '--button-active-text': hex(background),
    '--toolbar-bg': hex(secondary),
    '--toolbar-border': hex(border),
    '--toolbar-shadow': `0 10px 24px rgba(${rgbChannels(shadow)}, ${darkTheme ? 0.32 : 0.2})`,
    '--shadow-rgb': rgbChannels(shadow),
    '--surface-shadow': `0 15px 35px rgba(${rgbChannels(shadow)}, ${darkTheme ? 0.45 : 0.2})`,
    '--input-bg': `rgba(${overlayChannel}, ${darkTheme ? 0.06 : 0.04})`,
    '--white-rgb': '255, 255, 255',
    '--black-rgb': '0, 0, 0',
  }

  return {
    tokens,
    editorInk: foregroundInput,
    canvasContrast,
    canvasPasses: canvasContrast >= textTarget,
    recipeDiagnostics: recipeResult.diagnostics,
    contrastAdjustmentCount,
  }
}
